import { formatFileSize, updateBy } from '@toggle/helpers';
import { t } from 'i18next';

import { create } from '~/utils/store/createStore';

import { uploadFiles } from '../services/user-document-service';
import {
  allowedTypesMimeType,
  getMaxFileSize,
} from '../utils/formatUploadFiles';
import { UploadedFile, UserUploadStore } from './use-user-upload.types';

const CHUNK_SIZE = 5;
export const MAX_FILES_PER_QUESTION = 5;
export const MAX_FILES_PER_CONVERSATION = 10;

export const useUserUpload = create<UserUploadStore>((set, get) => ({
  files: [],
  alertOpen: false,
  errorMessage: undefined,
  toggleAlertOpen: (open: boolean) => set({ alertOpen: open }),
  addFiles: (newFiles: UploadedFile[]) => {
    const { files, toggleAlertOpen, setErrorMessage, initializeAlert } = get();

    initializeAlert();

    const uploadedFileCount = files.filter(f => f.status === 'complete').length;
    const totalFileCount = uploadedFileCount + newFiles.length;

    if (totalFileCount > MAX_FILES_PER_QUESTION) {
      toggleAlertOpen(true);
      setErrorMessage(t('chat:userUpload.tooManySelections'));
      return;
    }

    set(state => ({
      files: [...state.files, ...newFiles],
    }));

    get().uploadFiles(newFiles);
  },
  uploadFiles: async (files: UploadedFile[]) => {
    const { updateFile, toggleAlertOpen } = get();

    const invalidFiles = files.filter(
      file =>
        !allowedTypesMimeType.includes(file.fileType) ||
        file.sizeInBytes > getMaxFileSize(file.fileType)
    );
    const validFiles = files.filter(
      file =>
        allowedTypesMimeType.includes(file.fileType) &&
        file.sizeInBytes <= getMaxFileSize(file.fileType)
    );

    if (invalidFiles.length) {
      toggleAlertOpen(true);

      const locales = {
        byte: t('chat:fileSizes.byte'),
        kilobyte: t('chat:fileSizes.kilobyte'),
        megabyte: t('chat:fileSizes.megabyte'),
        gigabyte: t('chat:fileSizes.gigabyte'),
        terabyte: t('chat:fileSizes.terabyte'),
      };

      const invalidTypeFilesWithError = invalidFiles.map(
        (file): UploadedFile => {
          const maxSizeInBytes = getMaxFileSize(file.fileType);
          const formattedLimit = formatFileSize({
            sizeInBytes: maxSizeInBytes,
            decimals: 0,
            locales,
          });

          return {
            ...file,
            status: 'error',
            errorMessage: !allowedTypesMimeType.includes(file.fileType)
              ? t('chat:userUpload.invalidFileType')
              : t('chat:userUpload.fileSizeExceedsLimit', {
                  sizeLimit: formattedLimit,
                }),
          };
        }
      );

      invalidTypeFilesWithError.forEach(file => {
        updateFile(file);
      });
    }

    for (let i = 0; i < validFiles.length; i += CHUNK_SIZE) {
      const chunk = validFiles.slice(i, i + CHUNK_SIZE);

      const { data: response, error } = await uploadFiles(chunk);

      const resultMap = new Map(
        response?.results.map(r => [r.field_name, r]) || []
      );

      chunk.forEach(file => {
        const result = resultMap.get(file.field_name);
        const hasError = error || !result?.success;
        const errorMessage =
          error?.message ||
          result?.error ||
          t('chat:userUpload.failedToUpload');

        const updatedFile: UploadedFile = {
          ...file,
          id: result?.id || file.id,
          status: hasError ? 'error' : 'complete',
          errorMessage: hasError ? errorMessage : undefined,
        };

        updateFile(updatedFile);

        if (hasError) {
          toggleAlertOpen(true);
        }
      });
    }
  },
  removeFile: (field_name: string) => {
    // clean up the thumbnail url to prevent memory leak
    const file = get().files.find(f => f.field_name === field_name);

    if (file?.thumbnailUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(file.thumbnailUrl);
    }

    set(state => ({
      files: state.files.filter(file => file.field_name !== field_name),
    }));
  },
  updateFile: (file: UploadedFile) => {
    set(state => ({
      files: updateBy(
        state.files,
        { field_name: file.field_name },
        existingFile => ({
          ...existingFile,
          ...file,
        })
      ),
    }));
  },
  removeAllFiles: () => {
    // clean up the thumbnail urls to prevent memory leak
    get().files.forEach(file => {
      if (file.thumbnailUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(file.thumbnailUrl);
      }
    });

    set({ files: [] });
  },
  initializeAlert: () => {
    set({ alertOpen: false, errorMessage: undefined });
  },
  setErrorMessage: (errorMessage?: string) => {
    set({ errorMessage });
  },
}));
